import { Eye, EyeOff } from 'lucide-react';
import { type InputHTMLAttributes, useState } from 'react';
import { type FieldShellProps, FormField } from './FormField';

type NativeProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'id' | 'className' | 'required' | 'type'
>;

interface PasswordFieldProps extends FieldShellProps, NativeProps {}

/**
 * A password input with a reveal toggle, so a mistyped password can be checked
 * before submitting instead of costing a failed sign-in.
 */
export function PasswordField({
  label,
  hideLabel,
  error,
  helpText,
  required,
  className,
  ...input
}: PasswordFieldProps) {
  const [visible, setVisible] = useState(false);

  return (
    <FormField
      label={label}
      hideLabel={hideLabel}
      error={error}
      helpText={helpText}
      required={required}
      className={className}
    >
      {({ id, describedBy, invalid }) => (
        <div className="c-input">
          <input
            {...input}
            id={id}
            type={visible ? 'text' : 'password'}
            required={required}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            className="c-input__control"
          />
          <button
            type="button"
            className="c-input__toggle"
            onClick={() => setVisible((shown) => !shown)}
            aria-label="Show password"
            aria-pressed={visible}
          >
            {visible ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
          </button>
        </div>
      )}
    </FormField>
  );
}
